import { useState } from 'react';
import type { CarryForwardPayload, TrialBalanceRow } from '@/core/models/trial-balance.models';
import { notifyError } from '@/core/notify/notify';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import './ImportTrailBalance.scss';

export interface ImportTrailBalanceDialogData {
  /** The client's other trial balances; each shows its label field. */
  options: TrialBalanceRow[];
  /** The trial balance the figures are being carried into, named in the dialog's subtitle. */
  targetRefNo?: string;
}

/** Angular ImportTrailBalanceComponent: "Carry Forward Trial Balance". Closes with the CarryForwardPayload. */
export function ImportTrailBalance({
  data,
  dialogRef,
}: DialogComponentProps<ImportTrailBalanceDialogData, CarryForwardPayload>) {
  const options = data?.options ?? [];
  const targetRefNo = data?.targetRefNo;
  const [sourceTrialBalanceId, setSourceTrialBalanceId] = useState<string | null>(null);
  const [broughtForwardOnly, setBroughtForwardOnly] = useState(false);
  /** The dialog is closing with the choice: a second click cannot send it twice. */
  const [closing, setClosing] = useState(false);
  const selected = options.find((o) => o.id === sourceTrialBalanceId) ?? null;

  const onImport = () => {
    if (!sourceTrialBalanceId) {
      notifyError(
        options.length === 0
          ? 'This client has no other trial balance to import balances from.'
          : 'Select the trial balance to import balances from.',
      );
      return;
    }
    setClosing(true);
    dialogRef.close({ sourceTrialBalanceId, broughtForwardOnly: !!broughtForwardOnly });
  };

  return (
    <div className="mat-dialog-wrapper new-account tb-import-dialog">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-start justify-space-between">
                <div>
                  <h2 className="m-0">
                    <span>Carry Forward Trial Balance</span>
                  </h2>
                  {targetRefNo && <p className="tb-import-subtitle">Pull balances from another trial balance into {targetRefNo}</p>}
                </div>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field">
              <p>Trial Balance</p>
              <Select<TrialBalanceRow>
                className="w-387"
                name="sourceTrialBalanceId"
                items={options}
                bindLabel="label"
                bindValue="id"
                value={sourceTrialBalanceId}
                onChange={(value) => setSourceTrialBalanceId((value as string | null) ?? null)}
              />
            </div>

            {selected && (
              <div className="tb-import-preview">
                <div className="tb-import-preview__cell">
                  <div className="tb-import-preview__k">Period</div>
                  <div className="tb-import-preview__v">{selected.periodLabel || '-'}</div>
                </div>
                <div className="tb-import-preview__cell">
                  <div className="tb-import-preview__k">Lines</div>
                  <div className="tb-import-preview__v">{selected.lineCount} coded</div>
                </div>
                <div className="tb-import-preview__cell">
                  <div className="tb-import-preview__k">Status</div>
                  <div className="tb-import-preview__v">{selected.status || '-'}</div>
                </div>
              </div>
            )}

            <div className="tb-import-toggle">
              <Checkbox
                name="broughtForwardOnly"
                checked={broughtForwardOnly}
                onChange={(event) => setBroughtForwardOnly(event.target.checked)}
              >
                Brought Forward Only
              </Checkbox>
              <p className="tb-import-toggle__hint">
                Only carries over the balance-sheet accounts (assets, liabilities, reserves) - profit and loss
                accounts start at zero for the new period.
              </p>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" type="button" busy={closing} onClick={onImport}>
              Import Balance
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
